import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './component/header/header.component';
import { FooterComponent } from './component/footer/footer.component';
import { RouterModule } from '@angular/router';
import { SubNavbarComponent } from './component/sub-navbar/sub-navbar.component';
import { GradeSelectionComponent } from './component/grade-selection/grade-selection.component';
import { RangeSelectorComponent } from './component/range-selector/range-selector.component';
import { CourseCardComponent } from './component/course-card/course-card.component';
import { PlaygroundComponent } from './component/playground/playground.component';
import { CourseService } from './core/state/course/course.service';
import { PageLoaderService } from './util/pageloader.service';
import { UserService } from './core/state/users/users.service';
import { WebStorageService } from './util/web-storage.service';
import { StorageService } from './util/storage.service';
import {
  AuthGuardService,
  AuthorizationGuardService,
  AuthStatusGuardService,
} from './util/routes.guard';
import { GoogleDrivePickerService } from './core/services/google-drive-picker.service';
import { MultiFileUploadComponent } from './component/multi-file-upload/multi-file-upload.component';
import { FileUploadComponent } from './component/multi-file-upload/file-upload/file-upload.component';
import { FilePondModule } from 'ngx-filepond';
import { ResourceService } from './core/state/resource/resource.service';
import CourseCreatorService from './util/course-creator.service';
import { PaymentService } from './util/payment-api.service';
import { EmailPhoneComponent } from './component/email-phone/email-phone.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OtpComponent } from './component/otp/otp.component';
import { NgOtpInputModule } from 'ng-otp-input';
import { AuthControlComponent } from './component/header/auth-control/auth-control.component';
import CartService from './util/cart.service';
import { OrderService } from './core/state/order/order.service';
import { TooltipComponent } from './component/tooltip/tooltip.component';
import { StepperComponent } from './component/stepper/stepper.component';
import { LiveClassService } from './core/state/live-class/live-class.service';
import { BillingFormComponent } from './component/billing-form/billing-form.component';
import { EventCalendarComponent } from './component/event-calendar/event-calendar.component';

import { FullCalendarModule } from '@fullcalendar/angular';
import { SubscriptionService } from './core/state/subscription/subscription.service';
import { VerifyOtpComponent } from './component/verify-otp/verify-otp.component';
import { SocialShareComponent } from './component/social-share/social-share.component';
import { UserAvatarComponent } from './component/user-avatar/user-avatar.component';
import { UserInfoCardComponent } from './component/user-info-card/user-info-card.component';
import { AccountCardComponent } from './component/account-card/account-card.component';
import { AddRecordingsComponent } from './component/add-recordings/add-recordings.component';
import { FileUploadTrackerComponent } from './component/file-upload-tracker/file-upload-tracker.component';
import { MathCeilPipe } from './pipes/math-ceil.pipe';
import { RecordingCardComponent } from './component/recording-card/recording-card.component';
import { RecordingsComponent } from '../instructor/recordings/recordings.component';
import { StudyMaterialCardComponent } from './component/study-material-card/study-material-card.component';
import { SessionPickerComponent } from './component/session-picker/session-picker.component';

import { UiModule } from '@black/ui';
import { ConfirmDialogComponent } from './component/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogTriggerDirective } from './component/confirm-dialog/dialog-trigger.directive';
import { MeetingsListViewComponent } from './component/meetings-list-view/meetings-list-view.component';

import { CookieService } from './util/cookie.service';
import { ImageUploadComponent } from './component/image-upload/image-upload.component';
import { PwaInstallBannerComponent } from './component/pwa-install-banner/pwa-install-banner.component';
import { VideoPlayerComponent } from './component/video-player/video-player.component';
import { CookieConsentBannerComponent } from './component/cookie-consent-banner/cookie-consent-banner.component';
import { InviteStudentModalComponent } from './components/invite-student-modal/invite-student-modal.component';
import { AcademySettingsComponent } from '../dashboard/academy-settings/academy-settings.component';
import { NotificationBellComponent } from './components/notification-bell/notification-bell.component';
import { NotificationService } from './services/notification.service';

@NgModule({
  declarations: [
    AcademySettingsComponent,
    HeaderComponent,
    FooterComponent,
    SubNavbarComponent,
    GradeSelectionComponent,
    RangeSelectorComponent,
    CourseCardComponent,
    PlaygroundComponent,
    MultiFileUploadComponent,
    FileUploadComponent,
    EmailPhoneComponent,
    OtpComponent,
    AuthControlComponent,
    TooltipComponent,
    StepperComponent,
    BillingFormComponent,
    EventCalendarComponent,
    VerifyOtpComponent,
    SocialShareComponent,
    UserAvatarComponent,
    UserInfoCardComponent,
    AccountCardComponent,
    AddRecordingsComponent,
    FileUploadTrackerComponent,
    RecordingCardComponent,
    RecordingsComponent,
    StudyMaterialCardComponent,
    SessionPickerComponent,
    ConfirmDialogComponent,
    ConfirmDialogTriggerDirective,
    MeetingsListViewComponent,
    PwaInstallBannerComponent,
    CookieConsentBannerComponent,
    InviteStudentModalComponent,
    NotificationBellComponent,
  ],
  imports: [
    // Modules
    CommonModule,
    RouterModule,
    FilePondModule,
    FormsModule,
    ReactiveFormsModule,
    NgOtpInputModule,
    FullCalendarModule,
    UiModule,
    // Pipes
    MathCeilPipe,
    ImageUploadComponent,

    // Standalone Components
    VideoPlayerComponent,
  ],
  exports: [
    AcademySettingsComponent,
    HeaderComponent,
    FooterComponent,
    SubNavbarComponent,
    GradeSelectionComponent,
    RangeSelectorComponent,
    CourseCardComponent,
    MultiFileUploadComponent,
    FileUploadComponent,
    EmailPhoneComponent,
    OtpComponent,
    TooltipComponent,
    StepperComponent,
    BillingFormComponent,
    EventCalendarComponent,
    VerifyOtpComponent,
    SocialShareComponent,
    UserAvatarComponent,
    UserInfoCardComponent,
    AccountCardComponent,
    AddRecordingsComponent,
    FileUploadTrackerComponent,
    RecordingCardComponent,
    RecordingsComponent,
    StudyMaterialCardComponent,
    SessionPickerComponent,
    MeetingsListViewComponent,
    ConfirmDialogComponent,
    ConfirmDialogTriggerDirective,
    ImageUploadComponent,
    PwaInstallBannerComponent,
    CookieConsentBannerComponent,
    InviteStudentModalComponent,
    VideoPlayerComponent,
    NotificationBellComponent,
  ],
  providers: [
    NotificationService,
    CourseService,
    PageLoaderService,
    UserService,
    WebStorageService,
    StorageService,
    CookieService,
    AuthGuardService,
    AuthorizationGuardService,
    AuthStatusGuardService,
    ResourceService,
    CourseCreatorService,
    CartService,
    OrderService,
    LiveClassService,
    SubscriptionService,
    PaymentService,
    GoogleDrivePickerService,
  ],
})
export class SharedModule {}
